// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosGlobal from 'axios';

// project imports
import { dispatch } from '../index';
import axios from 'utils/axios';

// types
import { DefaultRootStateProps } from 'types';
import { openSnackbar } from './snackbar';
import { RecoverPasswordProps, RegisterUserSuccessProps, VendorProfileType } from 'types/slices/slices';
import { MenuDetailsType, NewProfileType, ProfileType, UserType } from 'types/components/security';

// ----------------------------------------------------------------------

const initialState: DefaultRootStateProps['security'] = {
    error: null,
    loading: true,
    usersList: [],
    currentUser: undefined,
    loadingEditInfo: true,
    profiles: [],
    vendors: [],
    approvalProfiles: [],
    fetching: false,
    menuOptions: [],
    vendorsAccess: []
};

const slice = createSlice({
    name: 'security',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },
        basicSuccess(state) {
            state.loading = false;
        },
        // USERS

        getUsersListPending(state) {
            state.loading = true;
        },
        getUsersListSuccess(state, action) {
            state.usersList = action.payload.map((item: UserType) => ({
                ...item,
                name: `${item.firstName} ${item.lastName ?? ''}`.trim(),
                avatart: 'default-profile.jpg'
            }));
            state.loading = false;
        },
        getUserDataPending(state) {
            state.loading = true;
        },
        getUserDataSuccess(state, action) {
            state.loading = false;
            state.currentUser = action.payload;
        },
        // profiles
        getEditInfoPending(state) {
            state.loadingEditInfo = true;
        },
        getVendorsSuccess(state, action) {
            const vendors: VendorProfileType[] = action.payload;
            state.loadingEditInfo = false;
            state.vendors = vendors;
        },
        getApprovalProfilesSuccess(state, action) {
            state.loadingEditInfo = false;
            state.approvalProfiles = action.payload;
        },
        // profile => edit
        updateProfilePending(state) {
            state.fetching = true;
        },
        updateProfileSuccess(state) {
            state.fetching = false;
        },
        deleteMenusServicePending(state) {
            state.fetching = true;
        },
        deleteMenusServiceSuccess(state) {
            state.fetching = false;
        },
        // user info
        updateUserInfoPending(state) {
            state.fetching = true;
        },
        updateUserInfoSuccess(state) {
            state.fetching = false;
        },
        // menu => access
        getMenuPermissionsSuccess(state, action) {
            const optionsList: MenuDetailsType[] = [];
            const payloadResponse: MenuDetailsType[] = action.payload;

            payloadResponse.forEach((optionItem) => {
                const exist = optionsList.some((item) => item.id === optionItem.id);
                if (!exist) {
                    optionsList.push(optionItem);
                }
            });

            state.menuOptions = optionsList;
            state.loading = false;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getProfiles.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProfiles.fulfilled, (state, action) => {
                state.loading = false;

                const profiles: ProfileType[] = action.payload;
                state.profiles = profiles;
            });
        builder
            .addCase(getVendorsAccess.pending, (state) => {
                state.loading = true;
            })
            .addCase(getVendorsAccess.fulfilled, (state, action) => {
                state.loading = false;
                state.vendorsAccess = action.payload;
            });
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------
export const { hasError } = slice.actions;

export function getUsersList(idMerchant?: string) {
    return async () => {
        dispatch(slice.actions.getUsersListPending());
        try {
            const response = await axios.get(`/loolu/api/user/searchall`, {
                /* baseURL: STYRK_API, */
                params: {
                    idMerchant: idMerchant || 1
                }
            });

            dispatch(slice.actions.getUsersListSuccess(response.data.response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function registerUserSuccess(registerProps: RegisterUserSuccessProps) {
    return async () => {
        try {
            await axios.post(`/loolu/api/auth/users`, registerProps, {
                /* baseURL: STYRK_API_AUTH_CREATE */
            });
            snackbarShow('User Successfully created', 'success');
        } catch (error: any) {
            dispatch(slice.actions.hasError(error));

            if (error?.response?.data?.code === 'auth/email-already-exists') {
                snackbarShow('The email address is already in use by another account.', 'error');
                return;
            }
            snackbarShow('An error occurred registering a new user', 'error');
        }
    };
}

export function getUserInfo(userId: number | null, idMerchant?: number | undefined) {
    return async () => {
        dispatch(slice.actions.getUserDataPending());

        try {
            const response = await axios.get(`/loolu/api/user/searchall`, {
                params: {
                    idMerchant: idMerchant || 1
                }
            });

            const users: UserType[] = response.data.response;

            const userFiteres = users.filter((userItem: UserType) => userItem.id === userId);

            if (userFiteres.length > 0) {
                const userClean = userFiteres.map((userData: UserType) => {
                    const {
                        id,
                        status,
                        user,
                        firstName,
                        lastName,
                        employeNumber,
                        email,
                        phoneNumber,
                        pass,
                        profile,
                        provider,
                        idApprovalProfile
                    } = userData;

                    return {
                        id,
                        status,
                        user,
                        firstName,
                        lastName,
                        employeNumber,
                        email,
                        phoneNumber,
                        pass,
                        profile,
                        provider,
                        idApprovalProfile
                    };
                });

                dispatch(slice.actions.getUserDataSuccess(userClean[0]));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const getProfiles = createAsyncThunk(`${slice.name}/getProfiles`, async ({ idMerchant }: { idMerchant?: number | string }) => {
    const response = await axios.get(`/loolu/api/profile/search`, {
        /* baseURL: STYRK_API, */
        params: {
            idMerchant
        }
    });
    return response.data.response;
});

export function getVendors(idMerchant?: number | string) {
    return async () => {
        dispatch(slice.actions.getEditInfoPending());

        try {
            const response = await axios.get(`/loolu/api/supplier/search`, {
                params: {
                    idMerchant
                }
            });

            dispatch(slice.actions.getVendorsSuccess(response.data.response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getApprovalProfiles(idMerchant?: number | string) {
    return async () => {
        dispatch(slice.actions.getEditInfoPending());

        try {
            const response = await axios.get(`/loolu/api/product/approvalprofile`, {
                params: {
                    idMerchant
                }
            });

            dispatch(slice.actions.getApprovalProfilesSuccess(response.data.response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}
export const updateUserInfo = createAsyncThunk(`${slice.name}/updateUserInfo`, async (dataSend: any) => {
    const response = await axios.post(`/loolu/api/user/save`, dataSend, {
        params: {
            idMerchant: dataSend.idMerchant
        }
    });
    return response.data;
});

export function updateNumber(registerProps: RegisterUserSuccessProps) {
    return async () => {
        try {
            await axios.patch(`/loolu/api/auth/users`, registerProps, {});
            snackbarShow('User Successfully updated', 'success');
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            snackbarShow('An error occurred updating user', 'error');
        }
    };
}

export function recoverPassword(recoverProps: RecoverPasswordProps) {
    return async () => {
        try {
            await axios.patch(`/loolu/api/auth/users/recoverPassword`, recoverProps);
            snackbarShow('Email sent, check your email.', 'success');
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            snackbarShow('An error occurred while sending the reset email', 'error');
        }
    };
}

export function getMenuPermissions(idMerchant?: number) {
    return async () => {
        try {
            const response = await axios.get(`/loolu/api/menu/search`, {
                params: {
                    idMerchant
                }
            });

            dispatch(slice.actions.getMenuPermissionsSuccess(response.data.response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}
export const createProfileService = createAsyncThunk(`${slice.name}/createProfileService`, async (data: NewProfileType) => {
    const response = await axios.post(`/loolu/api/profile/save`, data, {
        params: {
            idMerchant: data?.idMerchant
        }
    });
    return response.data;
});
export const getVendorsAccess = createAsyncThunk(`${slice.name}/getVendorsAccess`, async () => {
    const response = await axios.get(`/loolu/api/catalog/vendors/access`);
    return response.data;
});

export const createCatalogVendorsAccess = createAsyncThunk(
    `${slice.name}/createCatalogVendorsAccess`,
    async ({ id, action, IdPerfil, ID }: { id: number; action: string; IdPerfil: number; ID: number }) => {
        const response = await axios.post(`/loolu/api/catalog/vendors/access/${id}`, { action: action, IdPerfil: IdPerfil, ID: ID });
        return response.data;
    }
);
type Menus = {
    id: number;
    read: boolean;
    write: boolean;
};
type updateProfileServiceProps = {
    data: {
        idPerfil: number;
        idStatus: boolean;
        description: string;
        menus: Menus[];
        type: string;
    };
    idMerchant?: number;
};

export const updateProfileService = createAsyncThunk(
    `${slice.name}/updateProfileService`,
    async ({ data, idMerchant }: updateProfileServiceProps) => {
        const response = await axios.post(`/loolu/api/profile/save`, data, {
            params: {
                idMerchant
            }
        });
        return response.data;
    }
);

type deleteMenusServiceProps = {
    menus: number[];
    idPerfil: number;
    idMerchant?: number;
};

export const deleteMenusService = createAsyncThunk(
    `${slice.name}/deleteMenusService`,
    async ({ menus, idPerfil, idMerchant }: deleteMenusServiceProps) => {
        const promises = menus.map((item: number) =>
            axios.delete(`/loolu/api/profile/delete`, {
                params: {
                    idMerchant,
                    idMenu: `${item}`,
                    idPerfil
                }
            })
        );
        return async () => {
            try {
                await axiosGlobal.all(promises);
                return dispatch(slice.actions.deleteMenusServiceSuccess());
            } catch (error) {
                dispatch(slice.actions.hasError(error));
                return error;
            }
        };
    }
);

const snackbarShow = (message: string | unknown, variant: 'success' | 'error') => {
    dispatch(
        openSnackbar({
            message,
            variant
        })
    );
};
